import React, { useState, useRef, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import '../scss/awards.scss';
import { IoGridOutline, IoListOutline } from 'react-icons/io5';
import { achievements } from '../data/achievements';
import api from '../services/apiService';
import { FaCrown } from 'react-icons/fa';
import ProFeatureModal from './ProFeatureModal';
import AchievementModal from './AchievementModal';
import { authService } from '../services/authService';

const Awards = ({ defaultView = 'grid', showDummy = false, showHeader = false, seasonId = null }) => {
    
    const { playerId } = useParams();
    const sessionPlayerId = sessionStorage.getItem('player_id');
    const profileId = playerId || sessionPlayerId;
    const [viewMode, setViewMode] = useState('grid'); // Force grid view for dashboard
    const scrollContainerRef = useRef(null);
    const [earnedAchievements, setEarnedAchievements] = useState([]);
    const [achievementDates, setAchievementDates] = useState({}); // Store achievement dates
    const [teamId, setTeamId] = useState(null);
    const [teamType, setTeamType] = useState(null);
    const [latestAchievementId, setLatestAchievementId] = useState(null);
    const [showProModal, setShowProModal] = useState(false);
    const [showAchievementModal, setShowAchievementModal] = useState(false);
    const [selectedAchievement, setSelectedAchievement] = useState(null);
    const [flippingCoin, setFlippingCoin] = useState(null); // Track which coin is flipping
    const isManager = authService.isManager();
    const [subscriptionStatus, setSubscriptionStatus] = useState(null);
    const [isParent, setIsParent] = useState(false);

    useEffect(() => {
        const fetchTeamInfo = async () => {
            const teamId = sessionStorage.getItem('selected_team_id');
            const teamType = sessionStorage.getItem('selected_team_type');
            const userType = sessionStorage.getItem('user_type');
            const isParentRaw = sessionStorage.getItem('is_parent');
            
            const isParentUser = userType === 'parent' || isParentRaw === 'true';
            
            setIsParent(isParentUser);
            setTeamId(teamId);
            setTeamType(teamType);
        };

        fetchTeamInfo();
    }, []);

    const fetchSubscriptionStatus = async () => {
        try {
            const token = sessionStorage.getItem('token');
            const teamId = sessionStorage.getItem('selected_team_id');
            const url = teamId 
                ? `${process.env.REACT_APP_API_URL}/get_subscription_status.php?team_id=${teamId}`
                : `${process.env.REACT_APP_API_URL}/get_subscription_status.php`;
            
            const response = await api.get(url, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            if (response.data.success) {
                setSubscriptionStatus(response.data);
            }
        } catch (error) {
            console.error('Awards: Error fetching subscription status:', error);
        }
    };

    useEffect(() => {
        fetchSubscriptionStatus();
    }, []);



    useEffect(() => {
        // Show enhanced dummy achievements if showDummy is true (for any profile or user type)
        if (showDummy) {
            const dummyAchievements = [
                'first_strike',
                'goal-machine',
                'team-first',
                'magic-moment',
                'tireless-engine',
                'star_performer',
                'respect-award',
                'hat_trick', 
                'mvp', 
                'playmaker', 
                'never-gave-up'
            ];
            
            // Verify all dummy achievements exist in the achievements array
            const availableAchievementIds = achievements.map(a => a.id);
            const missingAchievements = dummyAchievements.filter(id => !availableAchievementIds.includes(id));
            if (missingAchievements.length > 0) {
                console.warn('Some dummy achievements are missing from achievements array:', missingAchievements);
            }
            setEarnedAchievements(dummyAchievements);
            setLatestAchievementId('magic-moment');
            
            // Create dummy dates for the achievements
            const dummyDates = {};
            dummyAchievements.forEach((achievementId, index) => {
                // Create dates going back 1-11 weeks from now
                const weeksAgo = index + 1;
                const date = new Date();
                date.setDate(date.getDate() - (weeksAgo * 7));
                dummyDates[achievementId] = date.toISOString();
            });
            setAchievementDates(dummyDates);
            return;
        }
        
        // For all other cases, fetch real achievements
        const fetchAchievements = async () => {
            try {
                const teamId = sessionStorage.getItem('selected_team_id');
                let url = `${process.env.REACT_APP_API_URL}/fetch_achievements.php?player_id=${profileId}&team_id=${teamId}`;
                
                // Add season_id if provided
                if (seasonId) {
                    url += `&season_id=${seasonId}`;
                }
                
                const response = await api.get(url);
                const data = response.data;
                
                if (data.success) {
                    const achievementIds = data.achievements.map(a => a.achievement_id);
                    setEarnedAchievements(achievementIds);
                    
                    // Store achievement dates
                    const dates = {};
                    data.achievements.forEach(achievement => {
                        dates[achievement.achievement_id] = achievement.created_at;
                    });
                    setAchievementDates(dates);
                    
                    if (data.achievements.length > 0) {
                        setLatestAchievementId(data.achievements[0].achievement_id);
                    } else {
                        setLatestAchievementId(null);
                    }
                } else {
                    // Clear achievements if none were returned
                    setEarnedAchievements([]);
                    setAchievementDates({});
                    setLatestAchievementId(null);
                }
            } catch (error) {
                console.error('[Awards] Error fetching achievements:', error);
                // Clear achievements on error
                setEarnedAchievements([]);
                setAchievementDates({});
                setLatestAchievementId(null);
            }
        };

        fetchAchievements();
    }, [profileId, showDummy, isParent, teamType, seasonId]);

    // Special effect to reset achievements when user type or showDummy changes
    useEffect(() => {
        // Don't clear achievements for profile 629 (demo profile) or when showing dummy data
        if (profileId === '629' || showDummy) {
            return;
        }
        
        // Clear the achievements immediately
        setEarnedAchievements([]);
        setAchievementDates({});
        setLatestAchievementId(null);
    }, [isParent, showDummy, profileId]);

    // Parent-specific achievements that should only be shown to parents with showDummy=true
    const PARENT_ONLY_ACHIEVEMENT_IDS = [
        'playmaker',
        'never-gave-up',
        'defensive-rock',
        'safe-hands',
        'skill-star',
        '100-percent',
        'most-improved',
        'bounceback',
        'captains-mindset',
        'positive-voice',
        'cool-pressure',
        'first-touch',
        'goal-machine',
        'team-first',
        'magic-moment',
        'tireless-engine',
        'respect-award'
    ];
    
    // Only these achievement IDs should be visible to ALL users (players and parents)
    const STANDARD_ACHIEVEMENT_IDS = [
        'iron_man',
        'hat_trick',
        'mvp',
        'hot_streak',
        'first_strike',
        'star_performer'
    ];

    // Get awards with their achievement status
    const awardsWithStatus = achievements
        .filter(award => {
            // For profile 629 (demo profile), show all achievements
            if (profileId === '629') {
                return true;
            }
            
            // For academy teams, show all achievements regardless of user type
            if (teamType === 'academy') {
                return true;
            }
            
            // For non-academy teams: non-parents only see standard achievements
            if (!isParent) {
                return STANDARD_ACHIEVEMENT_IDS.includes(award.id);
            }
            
            // For parents on non-academy teams, show all achievements
            return true;
        })
        .map(award => ({
            ...award,
            achieved: earnedAchievements.includes(award.id),
            isLatest: award.id === latestAchievementId
        }));



    const handleMouseDown = (e) => {
        // Only prevent default for clicks on the scroll container itself
        if (e.target === scrollContainerRef.current) {
            e.preventDefault(); 
        }
    };

    const handleTouchStart = (e) => {
        // Touch events are automatically handled by the browser
    };

    const handleCrownClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setShowProModal(true);
    };

    const handleAchievementClick = (award) => {
        // For non-pro users, only allow clicking on the latest achievement or unachieved ones
        const shouldBlurCoin = !effectiveShowDummy && !effectivePro && award.achieved && !award.isLatest;
        
        if (shouldBlurCoin) {
            // Show pro modal instead of achievement modal
            setShowProModal(true);
            return;
        }
        
        // Start flip animation immediately for instant feedback
        setFlippingCoin(award.id);
        
        // Prepare achievement data and open modal immediately
        const awardWithDate = {
            ...award,
            achievedDate: achievementDates[award.id] || null
        };
        setSelectedAchievement(awardWithDate);
        setShowAchievementModal(true);
        
        // Remove flip animation after a shorter duration
        setTimeout(() => {
            setFlippingCoin(null);
        }, 300); // Shorter animation for better responsiveness
    };

    // Consider both API subscription status and isManager when determining if premium features should be shown
    // Use API response data for Pro status instead of isPro prop for more accurate results
    const isProFromAPI = subscriptionStatus?.is_pro === true || subscriptionStatus?.has_active_subscription === true;
    const effectivePro = isProFromAPI || isManager;
    // Show dummy achievements if the user is a parent AND showDummy is true, OR if viewing profile 629
    const effectiveShowDummy = (isParent && showDummy) || (profileId === '629' && showDummy);
    



    const handleAchievementModalClose = () => {
        setShowAchievementModal(false);
        setSelectedAchievement(null);
    };

    return (
        <div className={`awards-container ${!showHeader ? 'dashboard-embed' : ''}`}>
            {showHeader && (
                <div className="awards-header container">
                    <h2 className="section-title">Achievements</h2>
                </div>
            )}
            {String(profileId) === '629' && showDummy && (
                <div className="dummy-data-notice global-notice">
                    <h4>Demo Profile</h4>
                    <p>This is a demo profile showing example achievements. Your real achievements will appear here once you've earned them.</p>
                </div>
            )}
            {!effectiveShowDummy && !effectivePro && subscriptionStatus !== null && (
                <div className="pro-upgrade-notice" onClick={handleCrownClick}>
                    <FaCrown className="crown-icon" />
                    <span>Upgrade to Pro to View All Achievements</span>
                </div>
            )}
            <div className="awards-wrapper">
                <div className="awards-grid">
                    {awardsWithStatus.map((award) => {
                        // For non-pro users, blur all achieved coins except the latest one
                        // Always blur if subscriptionStatus is null (still loading) and user is not a manager
                        // Once subscriptionStatus is loaded, use effectivePro to determine blurring
                        const shouldBlurCoin = !effectiveShowDummy && award.achieved && !award.isLatest && 
                            (subscriptionStatus === null ? !isManager : !effectivePro);
                        

                        
                        return (
                            <div 
                                key={award.id} 
                                className={`award-card ${award.achieved ? 'achieved' : 'locked'} ${award.isLatest ? 'latest' : ''} ${flippingCoin === award.id ? 'flipping' : ''} ${shouldBlurCoin ? 'pro-locked' : ''}`}
                                title={award.achieved ? 'Achieved!' : 'Not yet achieved'}
                                onClick={() => handleAchievementClick(award)}
                                style={{ cursor: 'pointer' }}
                            >
                                {award.isLatest && (
                                    <div className="latest-badge">Latest!</div>
                                )}
                                {shouldBlurCoin && (
                                    <>
                                        <div className="crown-wrapper" onClick={handleCrownClick}>
                                            <FaCrown className="crown-icon" />
                                        </div>
                                        <div className="blur-overlay"></div>
                                    </>
                                )}
                                <div className="coin-content">
                                    <div className="award-icon">
                                        {award.icon}
                                    </div>
                                    <div className="award-info">
                                        <h3>{award.title}</h3>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
            <ProFeatureModal 
                isOpen={showProModal} 
                onClose={() => setShowProModal(false)}
                featureName="Achievement History"
            />
            {showAchievementModal && selectedAchievement && (
                <AchievementModal 
                    achievement={selectedAchievement}
                    onClose={handleAchievementModalClose}
                    isLocked={!selectedAchievement.achieved}
                />
            )}
        </div>
    );
};

export default Awards;